import React, { useEffect, useRef, useState, useCallback } from "react";
import { HiFilter, HiSearch } from "react-icons/hi";
import { TbSquareRoundedCheck, TbSquareRoundedX } from "react-icons/tb";
import axios from "axios";
import RequestApprovedPopup from "./RequestApprovedPopup";
import RequestWarningPopup from "./RequestWarningpopup";
import TableLoader from "Components/Common/Loader/TableLoader";
import Pagination from "Components/Pagination/Pagination";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import { usePermission } from "Context/PermissionContext";

const RequestedGroupTable = ({ setCount }) => {
  const dropdownRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("requests");

  // Shared states for "All Group Requests"
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("All Request");
  const [warnPopupOpen, setWarnPopupOpen] = useState(false);
  const [approvedPopupOpen, setApprovedPopupOpen] = useState(false);
  const [warningMsg, setWarningMsg] = useState("");
  const [requestItem, setRequestItem] = useState(null);
  const [callRequest, setCallRequest] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination for all users
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);

  //pagination for active users
  const [activeCurrentPage, setActiveCurrentPage] = useState(1);
  const activeRowsPerPage = 5;
  const [activeTotalPages, setActiveTotalPages] = useState(1);

  // Active Groups tab states
  const [activeGroupsSearch, setActiveGroupsSearch] = useState("");
  const [activeGroups, setActiveGroups] = useState([]);
  const [loadingActive, setLoadingActive] = useState(false);

  const [buttonLoadingId, setButtonLoadingId] = useState(null);

  // const [offlineError, setOfflineError] = useState(null);
  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.userRequests?.subModule?.groupReq?.permissions;

  // ================= API Fetch for all calls =================
  const getCallRequest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/group-call/get-all-calls`,
        {
          params: {
            page: currentPage,
            limit: rowsPerPage,
            search: searchTerm,
            filter: selectedOption,
          },
        }
      );

      const data = response?.data?.data || [];
      setCallRequest(data);
      setTotalPages(response?.data?.pagination?.totalPages || 1);

      const { total, approved, rejected } = response?.data?.globalCounts || {};
      if (total !== undefined) {
        setCount({ total, approved, rejected });
      }
    } catch (err) {
      setError("Failed to load call requests. Please try again later.");
      setCallRequest([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchTerm, selectedOption]);

  // ================= Active Groups API =================

  const getActiveGroups = useCallback(async () => {
    setLoadingActive(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/group-call/groupactive`,
        {
          params: {
            isOnline: true,
            search: activeGroupsSearch,
            page: activeCurrentPage,
            limit: activeRowsPerPage,
          },
        }
      );

      // console.log(response)
      const data = response?.data?.data || [];
      setActiveGroups(data);
      setActiveTotalPages(response?.data?.pagination?.totalPages || 1);
    } catch (err) {
      setActiveGroups([]);
    } finally {
      setLoadingActive(false);
    }
  }, [activeGroupsSearch, activeCurrentPage, activeRowsPerPage]);

  // ================= DEACTIVATE Groups API =================
  const handleMakeOffline = async (groupId) => {
    setButtonLoadingId(groupId); // track which button is clicked
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/group-call/group-deactivate/${groupId}`
      );
      await getActiveGroups();
    } catch (err) {
      console.error("Failed to make group offline:", err);
    } finally {
      setButtonLoadingId(null); // reset after API call completes
    }
  };

  // const handleMakeOffline = async (groupId) => {
  //   try {
  //     console.log("Make offline clicked for groupId:", groupId);
  //     // Simulate delay
  //     await new Promise((resolve) => setTimeout(resolve, 500));
  //     console.log("Simulated API call successful");
  //     // Optionally, refresh the active groups list here if needed
  //     getActiveGroups();
  //   } catch (err) {
  //     console.error("Failed to make group offline:", err);
  //   }
  // };

  useEffect(() => {
    if (activeTab === "active") {
      const delayDebounce = setTimeout(() => {
        getActiveGroups();
      }, 500);

      return () => clearTimeout(delayDebounce);
    }
  }, [activeGroupsSearch, activeTab, getActiveGroups]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (activeTab === "requests") {
        getCallRequest();
      }
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, currentPage, selectedOption, getCallRequest, activeTab]);

  // ================= Handlers =================
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    if (value) {
      setSelectedOption("All Request");
    }
  };

  const handleActiveGroupsSearch = (e) => {
    setActiveGroupsSearch(e.target.value);
    setActiveCurrentPage(1);
  };

  const handleOpenWarning = (msg, row) => {
    setWarningMsg(msg);
    setRequestItem(row);
    setWarnPopupOpen(true);
  };

  const handleOpenApproved = (msg, row) => {
    setWarningMsg(msg);
    setRequestItem(row);
    setApprovedPopupOpen(true);
  };

  const handleCloseWarning = () => {
    setWarningMsg("");
    setWarnPopupOpen(false);
    getCallRequest();
  };

  const handleCloseApproved = () => {
    setWarningMsg("");
    setApprovedPopupOpen(false);
    getCallRequest();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setIsDropdownOpen(false);
    setCurrentPage(1);
    if (option !== "All Request") {
      setSearchTerm("");
    }
  };

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    // Clear search input when switching tabs
    setSearchTerm("");
    setActiveGroupsSearch("");
    setCurrentPage(1);
    setActiveCurrentPage(1);
  }, [activeTab]);

  // ================= Render =================
  return (
    <>
      <RequestWarningPopup
        requestItem={requestItem}
        warningMsg={warningMsg}
        warnPopupOpen={warnPopupOpen}
        handleClose={handleCloseWarning}
      />

      <RequestApprovedPopup
        requestItem={requestItem}
        warningMsg={warningMsg}
        approvedPopupOpen={approvedPopupOpen}
        handleCloseApproved={handleCloseApproved}
      />

      {/* Tabs */}
      <div className="my-4 flex gap-4 pb-2">
        <button
          className={`rounded-md px-4 py-2 shadow-sm ${
            activeTab === "requests"
              ? "bg-[#c51369] text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
          onClick={() => setActiveTab("requests")}
        >
          All Group Requests
        </button>
        <button
          className={`rounded-md px-4 py-2 shadow-sm ${
            activeTab === "active"
              ? "bg-[#c51369] text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
          onClick={() => setActiveTab("active")}
        >
          Active Groups
        </button>
      </div>

      {/* ================= TAB CONTENT ================= */}
      {activeTab === "requests" ? (
        // Existing All Requests UI
        <div className="py-2">
          {/* Filter + Search */}
          <div className="bo flex flex-col items-center justify-between space-y-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:space-y-0">
            {/* Filter Dropdown */}
            <div
              className="relative w-full md:w-[20%] md:min-w-[180px]"
              ref={dropdownRef}
            >
              <button
                className="flex w-full items-center rounded-md border bg-white px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                onClick={toggleDropdown}
              >
                <HiFilter color="#c51369" className="mr-2 h-5 w-5" />
                {selectedOption}
              </button>

              {isDropdownOpen && (
                <div className="absolute left-0 z-10 mt-2 w-full min-w-max rounded-md border bg-white shadow-lg md:w-auto">
                  <ul className="py-2">
                    {[
                      "All Request",
                      "Today's Request",
                      "Last 7 day's Request",
                      "All Approved",
                      "All Rejected",
                      "All Pending",
                    ].map((option) => (
                      <li
                        key={option}
                        className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                        onClick={() => handleOptionClick(option)}
                      >
                        {option}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Search Bar */}
            <div className="w-full md:mx-4 md:max-w-[40%] md:flex-1">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  <HiSearch color="#c51369" className="h-5 w-5" />
                </span>
                <input
                  type="text"
                  placeholder="Search by group name/number/Language..."
                  className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="mx-auto mt-5 w-auto rounded-t-2xl">
            <div className="overflow-x-auto rounded-t-2xl">
              <table className="w-full rounded-t-2xl text-left text-sm">
                {loading ? (
                  <tbody>
                    <tr>
                      <td colSpan="8" className="p-0">
                        <TableLoader />
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <>
                    <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                      <tr className="text-center">
                        <th className="px-6 py-3">Sl No.</th>
                        <th className="px-6 py-3">Group Name</th>
                        <th className="px-6 py-3">Language</th>
                        <th className="px-6 py-3">Create By</th>
                        <th className="px-6 py-3">Gender</th>
                        <th className="px-6 py-3">Status</th>
                        <th className="px-6 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {callRequest.length > 0 ? (
                        callRequest.map((row, index) => (
                          <tr
                            className="text-md bg-white text-center tracking-wide text-gray-700"
                            key={`${row._id}-${index}`}
                          >
                            <td className="border px-6 py-4">
                              {(currentPage - 1) * rowsPerPage + index + 1}
                            </td>
                            <td className="border px-6 py-4 capitalize">
                              {row.groupName}
                            </td>
                            <td className="border px-6 py-4 capitalize">
                              {row.groupLanguage}
                            </td>
                            <td className="border px-6 py-4 capitalize">
                              {row.createdBy?.nickName || ""}
                              {/* <br />
                              {row.createdBy?.phone || ""} */}
                            </td>
                            <td className="border px-6 py-4 capitalize">
                              {row.createdBy?.gender || ""}
                            </td>
                            <td className="border px-6 py-4 capitalize">
                              {row?.groupRequestAccepted === "pending" ? (
                                <span className="font-semibold text-orange-600">
                                  Pending
                                </span>
                              ) : row?.groupRequestAccepted === "accepted" ? (
                                <span className="font-semibold text-green-600">
                                  Approved
                                </span>
                              ) : row?.groupRequestAccepted === "rejected" ? (
                                <span className="font-semibold text-red-600">
                                  Rejected
                                </span>
                              ) : (
                                ""
                              )}
                            </td>
                            <td className="border px-6 py-4 text-center capitalize">
                              <div className="flex items-center justify-center gap-4">
                                {currentTabPermissions?.edit && (
                                  <TbSquareRoundedCheck
                                    onClick={() =>
                                      handleOpenApproved("approve", row)
                                    }
                                    size={28}
                                    className="cursor-pointer text-[#c51369] hover:text-[#a31058]"
                                    title="Approve"
                                  />
                                )}
                                {currentTabPermissions?.delete && (
                                  <TbSquareRoundedX
                                    onClick={() =>
                                      handleOpenWarning("reject", row)
                                    }
                                    size={28}
                                    className="cursor-pointer text-gray-700 hover:text-gray-900"
                                    title="Reject"
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="8"
                            className="px-6 py-10 text-center text-gray-500"
                          >
                            No call requests found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </>
                )}
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      ) : (
        // ================= Active Groups Tab =================
        <div className="py-2">
          {/* Search Bar */}
          <div className="flex w-full items-center rounded-2xl bg-white p-5 shadow-sm">
            <div className="relative w-3/4 md:w-2/5">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                <HiSearch color="#c51369" className="h-5 w-5" />
              </span>
              <input
                type="text"
                placeholder="Search active groups/number/Language..."
                className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                value={activeGroupsSearch}
                onChange={handleActiveGroupsSearch}
              />
            </div>
          </div>

          {/* Table for active groups */}
          <div className="mx-auto mt-5 w-auto rounded-t-2xl">
            <div className="overflow-x-auto rounded-t-2xl">
              {loadingActive ? (
                <TableLoader columns={6} />
              ) : (
                <table className="w-full rounded-t-2xl text-left text-sm">
                  <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                    <tr className="text-center">
                      <th className="px-6 py-3">Sl No.</th>
                      <th className="px-6 py-3">Group Name</th>
                      <th className="px-6 py-3">Language</th>
                      <th className="px-6 py-3">Create By</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeGroups.length > 0 ? (
                      activeGroups.map((row, index) => (
                        <tr
                          key={row._id}
                          className="text-md bg-white text-center  tracking-wide text-gray-700"
                        >
                          <td className="border px-6 py-4">{index + 1}</td>
                          <td className="border px-6 py-4 capitalize">
                            {row.groupName}
                          </td>
                          <td className="border px-6 py-4 capitalize">
                            {row.groupLanguage}
                          </td>
                          <td className="border px-6 py-4 capitalize">
                            {row.createdBy?.fullName || row.createdBy?.nickName}
                            {/* <br />
                            {row.createdBy?.phone || ""} */}
                          </td>
                          <td className="border px-6 py-4 capitalize">
                            {row.isOnline ? (
                              <span className="flex items-center justify-center gap-2 text-green-600">
                                <span className="h-2 w-2 rounded-full bg-green-600"></span>
                                Online
                              </span>
                            ) : (
                              <span className="flex items-center justify-center gap-2 text-gray-500">
                                <span className="h-2 w-2 rounded-full bg-gray-400"></span>
                                Offline
                              </span>
                            )}
                          </td>
                          <td className="border px-6 py-4 text-center">
                            {currentTabPermissions?.edit && (
                              <button
                                onClick={() => handleMakeOffline(row._id)}
                                className="flex items-center justify-center rounded-md bg-red-500 px-4 py-1 text-white hover:bg-red-700"
                                disabled={buttonLoadingId === row._id}
                              >
                                {buttonLoadingId === row._id ? (
                                  <BtnLoader />
                                ) : (
                                  "Make Offline"
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-6 py-10 text-center text-gray-500"
                        >
                          No active groups found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-4 flex justify-center">
              <Pagination
                currentPage={activeCurrentPage}
                totalPages={activeTotalPages}
                onPageChange={(page) => setActiveCurrentPage(page)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestedGroupTable;
